/**
 * Global Error Collector for Bug Reporting
 * Intercepts console.error, fetch errors, and JS errors
 * Stores them in ring buffers for inclusion in bug reports
 */

interface CollectedError {
  timestamp: string;
  message: string;
  stack?: string;
}

interface NetworkError {
  timestamp: string;
  url: string;
  method: string;
  status: number;
  statusText: string;
}

const MAX_CONSOLE_ERRORS = 20;
const MAX_NETWORK_ERRORS = 10;
const MAX_JS_ERRORS = 10;

const consoleErrors: CollectedError[] = [];
const networkErrors: NetworkError[] = [];
const jsErrors: CollectedError[] = [];

let initialized = false;
let originalConsoleError: typeof console.error | null = null;
let originalFetch: typeof window.fetch | null = null;

function pushToRingBuffer<T>(buffer: T[], item: T, maxSize: number): void {
  buffer.push(item);
  if (buffer.length > maxSize) {
    buffer.shift();
  }
}

export function initErrorCollector(): void {
  if (initialized) return;
  initialized = true;

  // Intercept console.error
  originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    try {
      const message = args
        .map((a) => {
          if (a instanceof Error) return `${a.message}\n${a.stack || ''}`;
          if (typeof a === 'object') return JSON.stringify(a);
          return String(a);
        })
        .join(' ');

      pushToRingBuffer(
        consoleErrors,
        {
          timestamp: new Date().toISOString(),
          message: message.slice(0, 500),
        },
        MAX_CONSOLE_ERRORS
      );
    } catch {
      // Don't break if our interceptor fails
    }
    originalConsoleError!.apply(console, args);
  };

  // Intercept fetch for network errors
  originalFetch = window.fetch;
  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const url = typeof args[0] === 'string' ? args[0] : args[0] instanceof Request ? args[0].url : String(args[0]);
    const method = (args[1]?.method || 'GET').toUpperCase();

    try {
      const response = await originalFetch!.apply(window, args);
      if (!response.ok && response.status >= 400) {
        pushToRingBuffer(
          networkErrors,
          {
            timestamp: new Date().toISOString(),
            url: url.slice(0, 200),
            method,
            status: response.status,
            statusText: response.statusText,
          },
          MAX_NETWORK_ERRORS
        );
      }
      return response;
    } catch (error) {
      pushToRingBuffer(
        networkErrors,
        {
          timestamp: new Date().toISOString(),
          url: url.slice(0, 200),
          method,
          status: 0,
          statusText: error instanceof Error ? error.message : 'Network Error',
        },
        MAX_NETWORK_ERRORS
      );
      throw error;
    }
  };

  // Intercept window.onerror for JS errors
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    pushToRingBuffer(
      jsErrors,
      {
        timestamp: new Date().toISOString(),
        message: `${String(message)} at ${source}:${lineno}:${colno}`,
        stack: error?.stack?.slice(0, 500),
      },
      MAX_JS_ERRORS
    );
    if (typeof originalOnError === 'function') {
      return originalOnError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Intercept unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    pushToRingBuffer(
      jsErrors,
      {
        timestamp: new Date().toISOString(),
        message: reason instanceof Error ? reason.message : String(reason),
        stack: reason instanceof Error ? reason.stack?.slice(0, 500) : undefined,
      },
      MAX_JS_ERRORS
    );
  });
}

export function getCollectedErrors() {
  return {
    consoleErrors: [...consoleErrors],
    networkErrors: [...networkErrors],
    jsErrors: [...jsErrors],
  };
}

export function getDeviceInfo() {
  const nav = navigator as Navigator & { deviceMemory?: number };
  const perf = performance as Performance & { memory?: { usedJSHeapSize: number; totalJSHeapSize: number } };

  return {
    userAgent: navigator.userAgent,
    currentUrl: window.location.href,
    screenSize: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    pixelRatio: window.devicePixelRatio,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timestamp: new Date().toISOString(),
    platform: navigator.platform,
    cookiesEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    memory: perf.memory
      ? {
          usedMB: Math.round(perf.memory.usedJSHeapSize / 1048576),
          totalMB: Math.round(perf.memory.totalJSHeapSize / 1048576),
        }
      : undefined,
    deviceMemory: nav.deviceMemory,
    pageLoadTime: performance.timing
      ? performance.timing.loadEventEnd - performance.timing.navigationStart
      : undefined,
  };
}
